import {z} from "zod";

/** ---------- Enums ---------- */
export const RaidStatus = z.enum(["CREATED", "RUNNING", "CLOSED"]);
export const ResourceType = z.enum(["SPICE", "TITANIUM", "STRAVIDIUM"]);
/** ---------- Helpers ---------- */
// ISO-Strings vom Backend als Date einlesen (fehlerfreundlich)
const isoDate = z.coerce.date(); // akzeptiert ISO-String oder Date

export const ParticipationIntervalSchema = z.object({
  id: z.number().int().nonnegative(),
  startTime: isoDate.nullable(),
  endTime: isoDate.nullable(),
});

export type ParticipationInterval = z.infer<typeof ParticipationIntervalSchema>;

/** ---------- Participant ---------- */
export const ParticipantSchema = z.object({
  id: z.number().int().nonnegative(),
  name: z.string(),
  intervals: z.array(ParticipationIntervalSchema),
  stravidium: z.number().int().nonnegative().nullable().optional(),
  titanium: z.number().int().nonnegative().nullable().optional(),
  spice: z.number().int().nonnegative().nullable().optional(),
  resourcesRetrieved: z.boolean().nullable().optional(),
});

export type Participant = z.infer<typeof ParticipantSchema>;

/** ---------- Resource ---------- */
export const ResourceSchema = z
.object({
  type: ResourceType,
  amount: z.number().int(),
})
.strict();

export type Resource = z.infer<typeof ResourceSchema>;

/** ---------- Raid ---------- */
export const RaidSchema = z.object({
  id: z.number().int().nonnegative(),
  name: z.string(),
  createdOn: isoDate.nullable().optional(),
  lastEditOn: isoDate.nullable().optional(),
  startTime: isoDate.nullable().optional(),
  endTime: isoDate.nullable().optional(),
  participants: z.array(ParticipantSchema),
  resources: z.array(ResourceSchema).nonoptional(),
  version: z.number().int().nonnegative(),

  // read-only Summaries vom Backend (trotz Name: numerische Felder)
  stravidiumReadOnly: z.number().int().nonnegative(),
  titaniumReadOnly: z.number().int().nonnegative(),
  spiceReadOnly: z.number().int().nonnegative(),

  status: RaidStatus,
});

export type Raid = z.infer<typeof RaidSchema>;

/** POST /raids (create): volles Raid plus einmalige Admin-PIN */
export const CreatedRaidSchema = RaidSchema.extend({
  adminPin: z.string(),
});
export type CreatedRaid = z.infer<typeof CreatedRaidSchema>;

export const AdminAccessSchema = z.object({
  adminPin: z.string(),
});
export type AdminAccess = z.infer<typeof AdminAccessSchema>;

export const AuthorizePinBodySchema = z.object({
  pin: z.string().min(1),
});

export const RaidsSchema = z.array(RaidSchema);
export type Raids = z.infer<typeof RaidsSchema>;

export const FavoritesSchema = z.object({
  top5: z.array(RaidSchema),
  runningRaids: z.array(RaidSchema),
});
export type Favorites = z.infer<typeof FavoritesSchema>;

/** ---------- Melange preview (POST /melange-preview) ---------- */
export const ParticipantMelangeAllocationSchema = z.object({
  participantId: z.number().int().nonnegative(),
  name: z.string().nullable().optional(),
  activeMinutesInRaid: z.number().int().nonnegative(),
  spiceSand: z.number().int().nonnegative(),
  melange: z.number().int().nonnegative(),
  spiceResidue: z.number().int().nonnegative(),
});
export type ParticipantMelangeAllocation = z.infer<typeof ParticipantMelangeAllocationSchema>;

export const MelangeDistributionResultSchema = z.object({
  conversionRuns: z.number().int().nonnegative(),
  sandPerRun: z.number().int().nonnegative(),
  sandConsumed: z.number().int().nonnegative(),
  remainingSand: z.number().int().nonnegative(),
  totalMelange: z.number().int().nonnegative(),
  totalSpiceResidue: z.number().int().nonnegative(),
  effectiveEndTime: isoDate,
  allParticipationWeightsZero: z.boolean(),
  participants: z.array(ParticipantMelangeAllocationSchema),
});
export type MelangeDistributionResult = z.infer<typeof MelangeDistributionResultSchema>;

export const MelangePreviewRequestSchema = z.object({
  raid: RaidSchema,
  totalSpiceSand: z.number().int().nonnegative(),
  landsraatBonus: z.boolean(),
  bonusConversionCount: z.number().int().nonnegative(),
  withoutTime: z.boolean().optional().default(false),
});
export type MelangePreviewRequest = z.infer<typeof MelangePreviewRequestSchema>;
